import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, PlayCircle, ClipboardList, MessageSquare, Award, Loader2, Download, Lock } from "lucide-react";
import { api, apiErr, fileUrl, openPdf } from "@/lib/api";
import { toast } from "sonner";
import { Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const STEPS = [
  { key: "overview", label: "研修概要", icon: ClipboardList },
  { key: "video", label: "動画", icon: PlayCircle },
  { key: "quiz", label: "理解度確認", icon: CheckCircle2 },
  { key: "reflection", label: "所感", icon: MessageSquare },
  { key: "done", label: "修了", icon: Award },
];

export default function TrainingPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState({});
  const [videoDone, setVideoDone] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [qIdx, setQIdx] = useState(0);
  const [reflection, setReflection] = useState({ understood: "", caution: "", improvement: "", other: "" });
  const [cert, setCert] = useState(null);
  const [busy, setBusy] = useState(false);
  const saveRef = useRef(0);
  const watchedRef = useRef({});
  const seekWarnRef = useRef(0);
  const videoElRef = useRef(null);
  const [speed, setSpeed] = useState(1);
  const applySpeed = (s) => { setSpeed(s); if (videoElRef.current) videoElRef.current.playbackRate = s; };

  const load = useCallback(async () => {
    const { data } = await api.get(`/trainings/${id}`);
    setT(data);
    const { data: prog } = await api.get(`/progress/${id}`);
    setProgress(prog);
    const done = {};
    Object.values(prog).forEach((p) => { if (p.completed) done[p.video_id] = true; });
    setVideoDone(done);
    const vids = data.videos || [];
    const firstUndone = vids.find((v) => !done[v.id]);
    setActiveId(firstUndone ? firstUndone.id : (vids[0]?.id ?? null));
    const { data: ref } = await api.get(`/reflections/${id}`);
    if (ref && ref.training_id) setReflection({ understood: ref.understood || "", caution: ref.caution || "", improvement: ref.improvement || "", other: ref.other || "" });
    const { data: att } = await api.get(`/quiz/attempts/${id}`);
    if (att.length) setQuizResult(att[0]);
    const { data: certs } = await api.get(`/certificates?fiscal_year=${data.fiscal_year}`);
    const c = certs.find((x) => x.training_id === id);
    if (c) setCert(c);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!t) return <Loading />;

  const saveProgress = (video, el, completed = false) => {
    api.post("/progress", {
      training_id: id, video_id: video.id, position: el.currentTime || 0,
      watched_seconds: el.currentTime || 0, percent: el.duration ? Math.round((el.currentTime / el.duration) * 100) : 0,
      completed,
    }).catch(() => {});
  };

  const warnSeek = () => {
    const n = Date.now();
    if (n - seekWarnRef.current > 2500) { seekWarnRef.current = n; toast.error("動画の早送りはできません。まだ視聴していない箇所へは飛ばせません（元の位置に戻しました）"); }
  };

  const onSeeking = (video, e) => {
    const el = e.target;
    if (videoDone[video.id]) return;
    const maxW = watchedRef.current[video.id] || 0;
    if (el.currentTime > maxW + 1.5) { try { el.currentTime = maxW; } catch {} warnSeek(); }
  };

  const onTime = (video, e) => {
    const el = e.target;
    if (!videoDone[video.id]) {
      const maxW = watchedRef.current[video.id] || 0;
      const tol = 1.5 * (el.playbackRate || 1);
      if (el.currentTime > maxW + tol) { try { el.currentTime = maxW; } catch {} warnSeek(); }
      else watchedRef.current[video.id] = Math.max(maxW, el.currentTime);
    }
    const now = Date.now();
    if (now - saveRef.current > 8000) { saveRef.current = now; saveProgress(video, el); }
  };

  const onEnded = (video, e) => {
    if (videoDone[video.id]) return;
    saveProgress(video, e.target, true);
    setVideoDone((d) => ({ ...d, [video.id]: true }));
    toast.success(`「${video.title}」を視聴完了しました`);
    const vids = t.videos || [];
    const idx = vids.findIndex((v) => v.id === video.id);
    if (idx >= 0 && idx + 1 < vids.length) setActiveId(vids[idx + 1].id);
  };

  const onLoaded = (video, e) => {
    videoElRef.current = e.target;
    e.target.playbackRate = speed;
    const saved = progress[video.id];
    if (saved && saved.last_position && !saved.completed) { try { e.target.currentTime = saved.last_position; } catch {} watchedRef.current[video.id] = saved.last_position; }
    if (videoDone[video.id]) watchedRef.current[video.id] = e.target.duration || 999999;
  };

  const allVideosDone = (t.videos || []).length === 0 || (t.videos || []).every((v) => videoDone[v.id]);

  const submitQuiz = async () => {
    const arr = (t.questions || []).map((q) => ({
      question_id: q.id,
      selected_option_ids: answers[q.id]?.selected_option_ids || [],
      bool_answer: answers[q.id]?.bool_answer,
      text_answer: answers[q.id]?.text_answer || "",
    }));
    setBusy(true);
    try {
      const { data } = await api.post("/quiz/submit", { training_id: id, answers: arr });
      setQuizResult(data);
      if (data.passed) toast.success(`合格しました（${data.score}点）`);
      else toast.error(`不合格です（${data.score}点 / 合格${data.pass_score}点）`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const submitReflection = async () => {
    setBusy(true);
    try {
      await api.post("/reflections", { training_id: id, ...reflection });
      toast.success("所感を提出しました");
      setStep(4);
      finishTraining();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const finishTraining = async () => {
    try {
      const { data } = await api.post(`/trainings/${id}/complete`);
      if (data.completed && data.certificate) { setCert(data.certificate); toast.success("研修を修了しました！"); }
    } catch {}
  };

  const setAns = (qid, patch) => setAnswers((a) => ({ ...a, [qid]: { ...a[qid], ...patch } }));

  const quizPassed = quizResult?.passed;
  const canQuiz = !t.require_quiz_pass || (t.questions || []).length === 0 || quizPassed;
  const quizList = t.questions || [];
  const curQ = quizList[qIdx];
  const qAnswered = (qq) => {
    if (!qq) return false;
    const a = answers[qq.id] || {};
    if (qq.qtype === "truefalse") return a.bool_answer === true || a.bool_answer === false;
    if (qq.qtype === "text") return !!(a.text_answer || "").trim();
    return (a.selected_option_ids || []).length > 0;
  };
  const hasReflection = reflection.understood || reflection.caution || reflection.improvement || reflection.other;

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3" data-testid="player-back">
        <ArrowLeft className="w-4 h-4" /> 戻る
      </button>

      {/* Step indicator */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 p-3 mb-6 overflow-x-auto">
        {STEPS.map((s, i) => (
          <button key={s.key} onClick={() => setStep(i)} data-testid={`step-${s.key}`}
            className={`flex flex-col items-center gap-1 px-2 min-w-[64px] ${i === step ? "text-blue-600" : i < step ? "text-green-600" : "text-slate-400"}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${i === step ? "bg-blue-100" : i < step ? "bg-green-100" : "bg-slate-100"}`}>
              <s.icon className="w-4 h-4" />
            </div>
            <span className="text-[11px] whitespace-nowrap">{s.label}</span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        {step === 0 && (
          <div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">{t.name}</h1>
            <div className="text-sm text-slate-500 mb-4">受講期限までに動画視聴・理解度確認・所感提出を完了してください</div>
            {t.purpose && <div className="mb-3"><Label>研修目的</Label><p className="text-sm text-slate-700 mt-1">{t.purpose}</p></div>}
            {t.description && <div className="mb-3"><Label>説明</Label><p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{t.description}</p></div>}
            {t.materials?.length > 0 && (
              <div className="mb-3"><Label>資料</Label>
                <ul className="mt-1 text-sm text-blue-600">{t.materials.map((m) => <li key={m.id}>・{m.title}</li>)}</ul>
              </div>
            )}
            <Button className="bg-blue-600 hover:bg-blue-700 mt-2" onClick={() => setStep(1)} data-testid="start-video-btn">動画を視聴する</Button>
          </div>
        )}

        {step === 1 && (() => {
          const vids = t.videos || [];
          const currentIndex = vids.findIndex((v) => !videoDone[v.id]);
          const selectable = (i) => currentIndex === -1 || videoDone[vids[i].id] || i === currentIndex;
          const activeVideo = vids.find((v) => v.id === activeId) || null;
          const activeIndex = vids.findIndex((v) => v.id === activeId);
          return (
            <div>
              <h2 className="font-semibold text-slate-800 mb-1">動画</h2>
              <p className="text-xs text-slate-500 mb-4">動画は上から順番に視聴します。前の動画を最後まで視聴すると、次の動画が再生できます。※ 早送り（未視聴箇所へのスキップ）はできません。巻き戻しは可能です。</p>

              {activeVideo ? (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">{activeIndex + 1}. {activeVideo.title}</span>
                    {videoDone[activeVideo.id] && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> 視聴完了</span>}
                  </div>
                  <video key={activeVideo.id} data-testid={`video-${activeVideo.id}`} src={fileUrl(activeVideo.url)} controls className="w-full rounded-lg bg-black aspect-video"
                    onLoadedMetadata={(e) => onLoaded(activeVideo, e)} onTimeUpdate={(e) => onTime(activeVideo, e)} onSeeking={(e) => onSeeking(activeVideo, e)} onEnded={(e) => onEnded(activeVideo, e)} onPause={(e) => saveProgress(activeVideo, e.target)} />
                  <div className="mt-2 flex items-center gap-2" data-testid="playback-speed">
                    <span className="text-xs text-slate-500">再生速度</span>
                    {[0.5, 1, 1.5, 2, 3].map((s) => (
                      <button key={s} onClick={() => applySpeed(s)} data-testid={`speed-${s}x`}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${speed === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>
                        {s}倍
                      </button>
                    ))}
                  </div>
                  {activeVideo.chapters?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {activeVideo.chapters.map((c) => <span key={c.id} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">{c.title}</span>)}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400 mb-6">視聴できる動画がありません</p>
              )}

              {/* 動画一覧（プレイリスト） */}
              <div className="text-xs font-semibold text-slate-500 mb-2">動画一覧（{vids.length}本）</div>
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden" data-testid="video-playlist">
                {vids.map((v, i) => {
                  const done = videoDone[v.id];
                  const isActive = v.id === activeId;
                  const canSelect = selectable(i);
                  return (
                    <button key={v.id} type="button" disabled={!canSelect} onClick={() => canSelect && setActiveId(v.id)}
                      data-testid={`video-item-${v.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors ${
                        isActive ? "bg-blue-50" : canSelect ? "hover:bg-slate-50" : "opacity-70 cursor-not-allowed"
                      }`}>
                      <span className="shrink-0">
                        {done ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                          : canSelect ? <PlayCircle className={`w-5 h-5 ${isActive ? "text-blue-600" : "text-slate-400"}`} />
                          : <Lock className="w-5 h-5 text-slate-300" data-testid={`video-locked-${v.id}`} />}
                      </span>
                      <span className={`flex-1 text-sm ${isActive ? "font-semibold text-blue-700" : "text-slate-700"}`}>{i + 1}. {v.title}</span>
                      <span className="text-xs shrink-0">
                        {done ? <span className="text-green-600">視聴完了</span>
                          : isActive ? <span className="text-blue-600">視聴中</span>
                          : canSelect ? <span className="text-slate-400">未視聴</span>
                          : <span className="text-slate-400 flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> ロック中</span>}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(0)}>戻る</Button>
                <Button className="bg-blue-600 hover:bg-blue-700" disabled={!allVideosDone} onClick={() => setStep(2)} data-testid="to-quiz-btn">
                  {allVideosDone ? "理解度確認へ進む" : "全ての動画を視聴してください"}
                </Button>
              </div>
            </div>
          );
        })()}

        {step === 2 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-800">理解度確認</h2>
              {quizList.length > 0 && <span className="text-sm text-slate-500" data-testid="quiz-progress">Q{qIdx + 1} / {quizList.length}</span>}
            </div>
            {quizList.length > 0 && <div className="h-1.5 bg-slate-100 rounded-full mb-5 overflow-hidden"><div className="h-full bg-blue-500 transition-all" style={{ width: `${((qIdx + 1) / quizList.length) * 100}%` }} /></div>}
            {(t.questions || []).length === 0 && <p className="text-sm text-slate-400 mb-4">この研修に理解度確認はありません</p>}
            {curQ && (
              <div key={curQ.id} className="mb-6" data-testid={`quiz-q-${curQ.id}`}>
                <div className="text-base font-medium text-slate-800 mb-3">Q{qIdx + 1}. {curQ.text}</div>
                {curQ.qtype === "single" && curQ.options.map((o) => (
                  <label key={o.id} className="flex items-center gap-2 py-2 px-3 rounded-lg border border-slate-200 mb-2 cursor-pointer hover:border-blue-300" data-testid={`opt-${o.id}`}>
                    <input type="radio" name={curQ.id} checked={(answers[curQ.id]?.selected_option_ids || [])[0] === o.id} onChange={() => setAns(curQ.id, { selected_option_ids: [o.id] })} />
                    <span className="text-sm text-slate-700">{o.label}</span>
                  </label>
                ))}
                {curQ.qtype === "multiple" && curQ.options.map((o) => {
                  const sel = answers[curQ.id]?.selected_option_ids || [];
                  return (
                    <label key={o.id} className="flex items-center gap-2 py-2 px-3 rounded-lg border border-slate-200 mb-2 cursor-pointer hover:border-blue-300" data-testid={`opt-${o.id}`}>
                      <input type="checkbox" checked={sel.includes(o.id)} onChange={(e) => setAns(curQ.id, { selected_option_ids: e.target.checked ? [...sel, o.id] : sel.filter((x) => x !== o.id) })} />
                      <span className="text-sm text-slate-700">{o.label}</span>
                    </label>
                  );
                })}
                {curQ.qtype === "truefalse" && (
                  <div className="flex gap-2 mt-1">
                    <Button size="sm" type="button" variant={answers[curQ.id]?.bool_answer === true ? "default" : "outline"} onClick={() => setAns(curQ.id, { bool_answer: true })} data-testid={`tf-o-${curQ.id}`}>○ 正しい</Button>
                    <Button size="sm" type="button" variant={answers[curQ.id]?.bool_answer === false ? "default" : "outline"} onClick={() => setAns(curQ.id, { bool_answer: false })} data-testid={`tf-x-${curQ.id}`}>× 誤り</Button>
                  </div>
                )}
                {curQ.qtype === "text" && (
                  <Textarea value={answers[curQ.id]?.text_answer || ""} onChange={(e) => setAns(curQ.id, { text_answer: e.target.value })} className="mt-1" placeholder="回答を入力" data-testid={`text-${curQ.id}`} />
                )}
              </div>
            )}
            {quizResult && (
              <div className={`rounded-lg p-3 mb-4 text-sm ${quizResult.passed ? "bg-green-50 text-green-700" : "bg-rose-50 text-rose-700"}`} data-testid="quiz-result">
                得点：{quizResult.score}点／{quizResult.passed ? "合格" : `不合格（合格${quizResult.pass_score ?? t.pass_score}点）`}
              </div>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => (qIdx > 0 ? setQIdx(qIdx - 1) : setStep(1))} data-testid="quiz-prev-btn">{qIdx > 0 ? "前の問題" : "戻る"}</Button>
              <div className="flex gap-2">
                {quizList.length === 0 || quizPassed ? (
                  <Button className="bg-blue-600 hover:bg-blue-700" disabled={!canQuiz} onClick={() => setStep(3)} data-testid="to-reflection-btn">所感入力へ進む</Button>
                ) : qIdx >= quizList.length - 1 ? (
                  <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitQuiz} disabled={busy || !qAnswered(curQ)} data-testid="submit-quiz-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "採点する"}</Button>
                ) : (
                  <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setQIdx(qIdx + 1)} disabled={!qAnswered(curQ)} data-testid="quiz-next-btn">次の問題</Button>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="font-semibold text-slate-800 mb-4">研修所感</h2>
            {[["understood", "理解できたこと"], ["caution", "今後注意すること"], ["improvement", "改善が必要だと感じたこと"], ["other", "その他意見"]].map(([k, label]) => (
              <div key={k} className="mb-3">
                <Label>{label}</Label>
                <Textarea value={reflection[k]} onChange={(e) => setReflection({ ...reflection, [k]: e.target.value })} className="mt-1" data-testid={`reflection-${k}`} />
              </div>
            ))}
            <div className="flex justify-between mt-2">
              <Button variant="outline" onClick={() => setStep(2)}>戻る</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" disabled={busy || !hasReflection} onClick={submitReflection} data-testid="submit-reflection-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "提出して修了判定へ"}
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="text-center py-6">
            {cert ? (
              <>
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"><Award className="w-8 h-8 text-green-600" /></div>
                <h2 className="text-xl font-bold text-slate-900 mb-1">研修を修了しました</h2>
                <p className="text-sm text-slate-500 mb-4">修了証番号：{cert.cert_no}</p>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => openPdf(`/reports/certificate/${cert.id}`)} data-testid="download-cert-btn">
                  <Download className="w-4 h-4 mr-1" /> 修了証をダウンロード
                </Button>
              </>
            ) : (
              <>
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-sm text-slate-500">修了条件を確認しています…（全動画視聴・理解度確認合格・所感提出）</p>
                <Button variant="outline" className="mt-4" onClick={finishTraining} data-testid="recheck-btn">修了判定を再確認</Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
